import { FEATURE_FLAG_KEYS } from '@alpha-core/feature-flags/feature-flag.constants';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FeatureFlagsService } from '@neutrinos/ngx-feature-flags';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { BranchStateService } from './branch-state.service';

interface IDomainUrl {
    type: string;
    value: {
        __alpha_env__: string;
    };
}
interface IApiOptions {
    headers?: HttpHeaders;
    params?: HttpParams;
    responseType?: string | 'json' | 'blob' | undefined;
    isFile?: boolean;
}
@Injectable({
    providedIn: 'root',
})
export class AlphaHttpClient {
    private httpClient = inject(HttpClient);
    private branchStateService = inject(BranchStateService);
    private featureFlagsService = inject(FeatureFlagsService);

    private baseUrl = environment.ALPHA_UI_MAKE_REQUEST_URL;
    private apiAuth = {
        authType: 'client',
        ids_url: {
            type: 'environment',
            value: {
                __alpha_env__: 'IDS_URL',
            },
        },
        path: '/token',
        method: 'post',
        grant_type: {
            type: 'string',
            value: 'client_credentials',
        },
        headers: [
            {
                key: 'Content-Type',
                value: 'application/x-www-form-urlencoded',
            },
        ],
        body: [
            {
                key: 'client_secret',
                type: 'environment',
                value: {
                    __alpha_env__: 'IDS_CLIENT_SECRET',
                },
            },
            {
                key: 'client_id',
                type: 'environment',
                value: {
                    __alpha_env__: 'IDS_CLIENT_ID',
                },
            },
        ],
    };
    private DEFAULT_ALPHA_SERVICES = [
        'ALPHA_UI_CONFIG_SERVICE_URL',
        'ALPHA_CONFIG_SERVICE_URL',
        'ALPHA_CASE_SERVICE_URL',
        'ALPHA_UI_CASE_SERVICE_URL',
        'ALPHA_MODULES_SERVICE_URL',
        'ALPHA_UI_MODULE_SERVICE_URL',
    ];

    get<T>(domainUrl: string | IDomainUrl, path: string, options?: IApiOptions): Observable<T> {
        const apiOptions = this.getDefaultApiOptions(domainUrl, path, 'get', options);
        return this.httpClient.post<T>(`${this.baseUrl}?${path}`, apiOptions, {
            responseType: options?.responseType as any,
        });
    }

    post<T>(domainUrl: string | IDomainUrl, path: string, body: any, options?: IApiOptions): Observable<T> {
        let file = false;
        if (options) {
            const { isFile } = options;
            file = isFile || false;
        }
        const apiOptions = this.getDefaultApiOptions(domainUrl, path, 'post', options, body, file);
        if (file) {
            Object.keys(apiOptions).forEach((key) => {
                const value = apiOptions[key];

                if (typeof value === 'string') {
                    body.append(key, value);
                } else {
                    body.append(key, JSON.stringify(value));
                }
            });
        }
        const req_body = file ? body : apiOptions;
        return this.httpClient.post<T>(`${this.baseUrl}?${path}`, req_body, {
            responseType: options?.responseType as any,
        });
    }

    put<T>(domainUrl: string | IDomainUrl, path: string, body: any, options?: IApiOptions): Observable<T> {
        const apiOptions = this.getDefaultApiOptions(domainUrl, path, 'put', options, body);
        return this.httpClient.post<T>(`${this.baseUrl}?${path}`, apiOptions, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }
    patch<T>(domainUrl: string | IDomainUrl, path: string, body: any, options?: IApiOptions): Observable<T> {
        const apiOptions = this.getDefaultApiOptions(domainUrl, path, 'patch', options, body);
        return this.httpClient.post<T>(`${this.baseUrl}?${path}`, apiOptions, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    delete<T>(domainUrl: string | IDomainUrl, path: string, options?: IApiOptions): Observable<T> {
        const apiOptions = this.getDefaultApiOptions(domainUrl, path, 'delete', options);
        return this.httpClient.post<T>(`${this.baseUrl}?${path}`, apiOptions, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    private getDefaultApiOptions(
        domainUrl: string | IDomainUrl,
        path: string,
        method: string,
        options?: IApiOptions,
        body?: any,
        isFile = false,
    ) {
        const skipAlphaAuth = environment.SKIP_ALPHA_AUTH === 'true';
        const isAlphaURL = this.isAlphaURL(domainUrl);

        // Build queryParams with branch injection for config service requests
        const queryParams: Record<string, any> = { ...(options?.params || {}) };
        const changeTrackingEnabled = this.featureFlagsService.isFeatureEnabled(FEATURE_FLAG_KEYS.CHANGE_TRACKING);
        if (changeTrackingEnabled) {
            const branch = this.branchStateService.getBranch();
            if (branch && isAlphaURL) {
                queryParams['branch'] = branch;
            }
        }

        const apiOptions: any = {
            headers: {
                ...options?.headers,
                ...(isFile && { 'Content-Type': 'application/x-www-form-urlencoded' }),
            },
            method: method,
            body: body || {},
            url: domainUrl,
            path: path,
            queryParams,
            isFormData: isFile ? 'true' : 'false',
        };

        if (!(skipAlphaAuth && isAlphaURL)) {
            apiOptions.apiAuth = this.apiAuth;
        }

        return apiOptions;
    }

    private isConfigServiceRequest(domainUrl: string | IDomainUrl): boolean {
        if (typeof domainUrl === 'string') {
            return domainUrl.includes('/configservice') || domainUrl.includes('CONFIG');
        }
        return domainUrl.value.__alpha_env__ === 'ALPHA_CONFIG_URL';
    }

    isAlphaURL(url: string | IDomainUrl) {
        if (typeof url === 'string') {
            return false;
        }
        let ALPHA_SERVICE_URLS: string[];
        try {
            ALPHA_SERVICE_URLS = JSON.parse(environment.ALPHA_SERVICES);
            if (!Array.isArray(ALPHA_SERVICE_URLS)) {
                throw new Error('Parsed value is not an array');
            }
        } catch (error) {
            console.error('Error parsing ALPHA_SERVICES from environment:', error);
            ALPHA_SERVICE_URLS = [
                'ALPHA_UI_CONFIG_SERVICE_URL',
                'ALPHA_UI_CASE_SERVICE_URL',
                'ALPHA_UI_MODULE_SERVICE_URL',
            ];
        }
        ALPHA_SERVICE_URLS = Array.from(new Set([...this.DEFAULT_ALPHA_SERVICES, ...ALPHA_SERVICE_URLS]));
        return url.type === 'environment' && ALPHA_SERVICE_URLS.includes(url.value.__alpha_env__);
    }
}
